/**
 * Turning API failures into plain sentences, and reading the Procedures error codes.
 *
 * The API puts the code the portal branches on in `errors[0]` (PROCEDURES_ERROR_CODES, e.g.
 * `sign_in_required`). The SDK throws a CCMSError carrying `status` and `errors`. Rate limiters
 * can answer 429 without a JSON body, so the status is checked as well.
 */
import { PROCEDURES_ERROR_CODES } from '@actschurch/ccms-sdk';

interface ErrorLike {
  code?: string;
  status?: number;
  message?: string;
  errors?: unknown;
  raw?: { errors?: unknown; message?: string } | unknown;
}

function asErrorLike(err: unknown): ErrorLike {
  return (err && typeof err === 'object' ? err : {}) as ErrorLike;
}

function errorList(err: unknown): string[] {
  const e = asErrorLike(err);
  const out: string[] = [];
  if (Array.isArray(e.errors)) out.push(...e.errors.filter((x): x is string => typeof x === 'string'));
  const raw = e.raw as { errors?: unknown } | undefined;
  if (raw && Array.isArray(raw.errors)) out.push(...raw.errors.filter((x): x is string => typeof x === 'string'));
  return out;
}

/** The Procedures error code in errors[0] (lower case), or null. */
export function procedureErrorCode(err: unknown): string | null {
  const list = errorList(err);
  const known = Object.values(PROCEDURES_ERROR_CODES) as string[];
  for (const item of list) {
    const key = item.trim().toLowerCase();
    if (known.includes(key)) return key;
  }
  // Some endpoints may only put the code in the message.
  const msg = (asErrorLike(err).message ?? '').trim().toLowerCase();
  if (known.includes(msg)) return msg;
  if (asErrorLike(err).status === 429) return PROCEDURES_ERROR_CODES.ChatRateLimited;
  return null;
}

export function hasProcedureError(err: unknown, code: string): boolean {
  return procedureErrorCode(err) === code;
}

export function errorStatus(err: unknown): number | undefined {
  return asErrorLike(err).status;
}

/** No signal, a timeout or the server never answered: worth retrying later. */
export function isNetworkError(err: unknown): boolean {
  const e = asErrorLike(err);
  if (e.code === 'NETWORK_ERROR' || e.code === 'TIMEOUT') return true;
  if (e.status === 408 || e.status === 502 || e.status === 503 || e.status === 504) return true;
  if (typeof e.message === 'string' && /failed to fetch|network|load failed|networkerror|econn|timeout/i.test(e.message)) return true;
  // A gateway answering HTML instead of JSON (proxy restarting).
  if (e.code === 'INVALID_RESPONSE' && (!e.status || e.status >= 500)) return true;
  return false;
}

/** Errors worth trying again later (network, server, rate limit). */
export function isRetryable(err: unknown): boolean {
  if (isNetworkError(err)) return true;
  const status = errorStatus(err);
  return status === 429 || (status !== undefined && status >= 500);
}

export function isNotFound(err: unknown): boolean {
  return errorStatus(err) === 404;
}

const CODE_MESSAGES: Record<string, string> = {
  sign_in_required: 'Please sign in to open this procedure.',
  not_assigned: 'This procedure has not been shared with you.',
  not_published: 'This procedure is not available right now.',
  public_runs_disabled: 'Running procedures without signing in is switched off at the moment. Please sign in to carry on.',
  run_token_invalid: "We couldn't find your progress for this run. Please start again.",
  run_closed: 'This run has already finished.',
  chat_disabled: 'Ask is switched off for this procedure right now.',
  chat_rate_limited: "You've asked a lot of questions in the last hour. Please try again a little later.",
  ai_unavailable: "The assistant isn't available right now. Please try again later.",
  recaptcha_failed: "We couldn't confirm you're a person. Please tick the box again and try once more.",
  media_storage_unavailable: "Photos and videos can't load right now. Please try again in a few minutes.",
};

/** A sentence a volunteer can act on (never a code or a stack trace). */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const code = procedureErrorCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (isNetworkError(err)) return "We couldn't reach the server. Check your signal and try again.";
  const status = errorStatus(err);
  if (status === 401) return 'Your sign-in has expired. Please sign in again.';
  if (status === 403) return "You don't have access to that.";
  if (status === 404) return "We couldn't find that. The link may be old.";
  if (status && status >= 500) return 'Something went wrong on our side. Please try again in a few minutes.';
  const e = asErrorLike(err);
  const candidates = [...errorList(err), typeof e.message === 'string' ? e.message : ''].filter(
    (c) => c && c.length < 200 && /\s/.test(c) && !/^request failed/i.test(c),
  );
  return candidates[0] || fallback;
}
